import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";

async function changeSalesNavigatorContractRequest(
  workspaceId: string,
  accountId: string,
  salesNavigatorContract: string,
): Promise<void> {
  await post(
    `workspaces/${workspaceId}/accounts/${accountId}/sales-navigator/selected-seat`,
    {
      name: salesNavigatorContract,
    },
  );
}

export default function useSalesNavigatorContractMutations() {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const queryClient = useQueryClient();
  const { accountsKeys } = getQueryKeys(workspaceId);

  const {
    mutateAsync: changeSalesNavigatorContract,
    isPending: isChangingSalesNavigatorContract,
  } = useMutation({
    mutationFn: ({
      accountId,
      salesNavigatorContract,
    }: {
      accountId: string;
      salesNavigatorContract: string;
    }) =>
      changeSalesNavigatorContractRequest(
        workspaceId,
        accountId,
        salesNavigatorContract,
      ),
    onMutate: ({ accountId, salesNavigatorContract }) => {
      const previousSalesNavigatorContract = queryClient.getQueryData<string>(
        accountsKeys.salesNavigatorContract(accountId),
      );

      queryClient.setQueryData(
        accountsKeys.salesNavigatorContract(accountId),
        salesNavigatorContract,
      );

      return previousSalesNavigatorContract;
    },
    onError: (_, { accountId }, previousSalesNavigatorContract) => {
      queryClient.setQueryData(
        accountsKeys.salesNavigatorContract(accountId),
        previousSalesNavigatorContract,
      );

      toast.error("Failed to change Sales Navigator contract");
    },
  });

  return {
    changeSalesNavigatorContract,
    isChangingSalesNavigatorContract,
  };
}
